import type { AxiosInstance } from 'axios';

import { fetchPaginatedList } from '@accio3d/shared-ui/services/pagination';
import type { PagePaginatedResponse, PaginationParams } from '@accio3d/shared-ui/types/pagination';
import type { ProcessCategory } from '@accio3d/shared-ui/data/manufacturingData';

export interface SupplierListItem {
  id: string;
  organization: {
    id: string;
    name: string;
    type: string;
    website: string | null;
    industry: string | null;
    dunsNumber: string | null;
  };
  location: string | null;
  tier: string;
  rating: number | null;
  ratingCount: number;
  certifications: string[];
  technologies: string[];
  materials: string[];
  equipmentClasses: string[];
  serviceTypes: string[];
  engineeringServices: string[];
}

export interface SupplierMatchParams extends PaginationParams {
  processCategory: ProcessCategory;
  materialFamily?: string;
  materialId?: number;
  /** Purchaser-facing process label, used only by the legacy profile fallback. */
  technology?: string;
  serviceType?: string;
  location?: string;
  search?: string;
  sort?: string;
  order?: string;
  includeProfileFallback?: boolean;
}

/** How a supplier qualified — declared capabilities, or the legacy profile text. */
export type SupplierMatchedOn = 'capability' | 'profile';

export interface MatchedSupplier extends SupplierListItem {
  matchedOn: SupplierMatchedOn;
}

export interface SupplierListParams extends PaginationParams {
  sort?: string;
  order?: string;
  search?: string;
  location?: string;
  tier?: string;
  technology?: string;
  material?: string;
  serviceType?: string;
  certification?: string;
}

export function createSuppliersService(client: AxiosInstance) {
  return {
    async list(params: SupplierListParams): Promise<PagePaginatedResponse<SupplierListItem>> {
      return fetchPaginatedList<SupplierListItem>(client, '/suppliers', params, {
        params: {
          sort: params.sort,
          order: params.order,
          search: params.search,
          location: params.location,
          tier: params.tier,
          technology: params.technology,
          material: params.material,
          serviceType: params.serviceType,
          certification: params.certification,
        },
      });
    },

    async match(params: SupplierMatchParams): Promise<PagePaginatedResponse<MatchedSupplier>> {
      return fetchPaginatedList<MatchedSupplier>(client, '/suppliers/match', params, {
        params: {
          processCategory: params.processCategory,
          materialFamily: params.materialFamily,
          materialId: params.materialId,
          technology: params.technology,
          serviceType: params.serviceType,
          location: params.location,
          search: params.search,
          sort: params.sort,
          order: params.order,
          includeProfileFallback: params.includeProfileFallback,
        },
      });
    },
  };
}

export type SuppliersService = ReturnType<typeof createSuppliersService>;
